import React, { forwardRef, useState, useRef, useEffect, useLayoutEffect } from 'react'
import { createPortal } from 'react-dom'
import classNames from 'classnames'
import { getInputBaseClasses, getInputLabelClasses, getInputHelperTextClasses } from '../utils/inputStyles'

export interface SelectOption {
  value: string
  label: string
  disabled?: boolean
}

export interface SelectProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> {
  value?: string
  onChange: (value: string) => void
  options: SelectOption[]
  label?: string
  placeholder?: string
  error?: boolean
  helperText?: string
  disabled?: boolean
  required?: boolean
  name?: string
}

const ChevronDownIcon = ({ className }: { className?: string }) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    fill="none" 
    viewBox="0 0 24 24" 
    strokeWidth={1.5} 
    stroke="currentColor" 
    className={className}
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
  </svg>
)

const CheckIcon = ({ className }: { className?: string }) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    fill="none" 
    viewBox="0 0 24 24" 
    strokeWidth={1.5} 
    stroke="currentColor" 
    className={className}
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
  </svg>
)

const Select = forwardRef<HTMLButtonElement, SelectProps>(
  ({ value, onChange, options, label, placeholder = 'Selecione uma opção', disabled, className = '', required, error, helperText, id, name, ...props }, ref) => {
    const [isOpen, setIsOpen] = useState(false)
    const [placement, setPlacement] = useState<'bottom' | 'top'>('bottom')
    const [coords, setCoords] = useState({ top: 0, left: 0, width: 0 })
    const containerRef = useRef<HTMLDivElement>(null)
    const listRef = useRef<HTMLUListElement>(null)
    const dropdownRef = useRef<HTMLDivElement>(null)
    
    // Close on click outside
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent | TouchEvent) => {
        if (!isOpen) return
        
        const target = event.target as Node
        const container = containerRef.current
        const dropdown = dropdownRef.current

        // Check if click is inside container or dropdown (since dropdown is in portal)
        if (
          container && container.contains(target) ||
          dropdown && dropdown.contains(target)
        ) {
          return
        }

        setIsOpen(false)
      }

      const handleScroll = (event: Event) => {
        if (!isOpen) return

        const target = event.target as HTMLElement
        const dropdown = dropdownRef.current

        // If scrolling inside the dropdown itself, don't close
        if (dropdown && dropdown.contains(target)) {
          return
        }

        // Only close if scrolling happens outside the dropdown (e.g. main window scroll)
        setIsOpen(false)
      }

      if (isOpen) {
        window.addEventListener('mousedown', handleClickOutside)
        window.addEventListener('touchstart', handleClickOutside)
        // Use capture=true to catch scroll events from any container, but filter them in the handler
        window.addEventListener('scroll', handleScroll, true)
        window.addEventListener('resize', handleScroll)
      }

      return () => {
        window.removeEventListener('mousedown', handleClickOutside)
        window.removeEventListener('touchstart', handleClickOutside)
        window.removeEventListener('scroll', handleScroll, true)
        window.removeEventListener('resize', handleScroll)
      }
    }, [isOpen])

    // Calculate position when opening
    useLayoutEffect(() => {
      if (isOpen && containerRef.current) {
        const updateCoords = () => {
          if (!containerRef.current) return
          const rect = containerRef.current.getBoundingClientRect()
          const viewportHeight = window.innerHeight
          const spaceBelow = viewportHeight - rect.bottom
          const spaceAbove = rect.top
          
          // Estimated height of dropdown (max-h-60 = 240px + padding/border)
          const estimatedHeight = 250
          
          const shouldOpenUpward = spaceBelow < estimatedHeight && spaceAbove > spaceBelow
          
          setPlacement(shouldOpenUpward ? 'top' : 'bottom')
          setCoords({
            top: shouldOpenUpward ? rect.top : rect.bottom,
            left: rect.left,
            width: rect.width
          })
        }

        updateCoords()
        // No need for window listeners here as they are in the other useEffect
      }
    }, [isOpen])

    const handleSelect = (optionValue: string) => {
      onChange(optionValue)
      setIsOpen(false)
    }

    const selectedOption = options.find(opt => opt.value === value)
    
    // Base styles from helper
    const baseInputClasses = getInputBaseClasses(error, disabled, false, classNames('bg-white flex items-center justify-between cursor-pointer relative', className))

    const toggleDropdown = () => {
      if (disabled) return
      
      if (!isOpen && containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect()
        const viewportHeight = window.innerHeight
        const spaceBelow = viewportHeight - rect.bottom
        const spaceAbove = rect.top
        const estimatedHeight = 250
        const shouldOpenUpward = spaceBelow < estimatedHeight && spaceAbove > spaceBelow
        
        setPlacement(shouldOpenUpward ? 'top' : 'bottom')
        setCoords({
          top: shouldOpenUpward ? rect.top : rect.bottom,
          left: rect.left,
          width: rect.width
        })
      }
      
      setIsOpen(!isOpen)
    }

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (disabled) return

      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        toggleDropdown()
      } else if (e.key === 'Escape') {
        setIsOpen(false)
      } else if (e.key === 'ArrowDown' && !isOpen) {
        e.preventDefault()
        toggleDropdown()
      }
    }

    const dropdownContent = (
      <div 
        ref={dropdownRef}
        className={classNames(
          "fixed z-[9999] max-h-60 overflow-auto overscroll-contain rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm animate-in fade-in zoom-in-95 duration-100",
          placement === 'bottom' ? 'mt-1 origin-top' : 'mb-1 -translate-y-full origin-bottom'
        )}
        style={{
          top: coords.top,
          left: coords.left,
          width: coords.width
        }}
      >
        <ul
          ref={listRef}
          role="listbox"
          className="outline-none"
        >
          {options.length === 0 ? (
            <li className="relative cursor-default select-none py-2 pl-3 pr-9 text-gray-500 italic">
              Nenhuma opção disponível
            </li>
          ) : (
            options.map((option) => {
              const isSelected = option.value === value
              const isDisabled = option.disabled

              return (
                <li
                  key={option.value}
                  className={classNames(
                    'relative cursor-pointer select-none py-2.5 pl-3 pr-9 transition-colors',
                    {
                      'bg-blue-50 text-blue-900': isSelected,
                      'text-gray-900 hover:bg-gray-100': !isSelected && !isDisabled,
                      'text-gray-400 cursor-not-allowed opacity-60': isDisabled,
                    }
                  )}
                  role="option"
                  aria-selected={isSelected}
                  aria-disabled={isDisabled}
                  onClick={(e) => {
                    e.stopPropagation()
                    if (!isDisabled) handleSelect(option.value)
                  }}
                >
                  <div className="flex items-center">
                    <span className={classNames('block truncate', { 'font-semibold': isSelected, 'font-normal': !isSelected })}>
                      {option.label}
                    </span>
                  </div>

                  {isSelected && (
                    <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-blue-600">
                      <CheckIcon className="h-5 w-5" />
                    </span>
                  )}
                </li>
              )
            })
          )}
        </ul>
      </div>
    )

    return (
      <div className="w-full relative" ref={containerRef}>
        {label && (
          <label 
            htmlFor={id} 
            className={getInputLabelClasses()}
            onClick={toggleDropdown}
          >
            {label} {required && <span className="text-red-500">*</span>}
          </label>
        )}
        
        <button
          ref={ref}
          type="button"
          id={id}
          name={name}
          className={baseInputClasses}
          onClick={toggleDropdown}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          aria-invalid={error || undefined}
          {...props as React.ButtonHTMLAttributes<HTMLButtonElement>}
        >
          <span className={classNames('block truncate', { 'text-gray-500': !selectedOption })}>
            {selectedOption ? selectedOption.label : placeholder}
          </span>
          <span className="pointer-events-none flex items-center pr-1">
            <ChevronDownIcon 
              className={classNames(
                "h-5 w-5 text-gray-400 transition-transform duration-200",
                { "transform rotate-180": isOpen }
              )} 
            />
          </span>
        </button>

        {isOpen && coords.width > 0 && createPortal(dropdownContent, document.body)}

        {helperText && (
          <p className={getInputHelperTextClasses(error)}>
            {helperText}
          </p>
        )}
      </div>
    )
  }
)

Select.displayName = 'Select'

export default Select
